import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import HeroSection from '@/components/HeroSection';
import ConditionsSection from '@/components/ConditionsSection';
import QuickHelpSection from '@/components/QuickHelpSection';
import ServicesSection from '@/components/ServicesSection';
import AboutSection from '@/components/AboutSection';
import RehabPackagesSection from '@/components/RehabPackagesSection';
import TestimonialsSection from '@/components/TestimonialsSection';
import PartnersSection from '@/components/PartnersSection';
import ContactSection from '@/components/ContactSection';
import Footer from '@/components/Footer';
import BookingModal from '@/components/BookingModal';
import PricingModal from '@/components/PricingModal';
import MonthlyProgramBanner from '@/components/MonthlyProgramBanner';
import CookieBanner from '@/components/CookieBanner';

const HomePage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [prefilledSlot, setPrefilledSlot] = useState(null);
  const [searchParams] = useSearchParams();

  // Open booking if ?book=true in URL (from CTA / quick-booking pill on SEO pages)
  useEffect(() => {
    if (searchParams.get('book') === 'true') {
      // Read optional pre-filled slot params from URL
      const date = searchParams.get('date');
      const time = searchParams.get('time');
      const therapist = searchParams.get('therapist');
      if (date && time) {
        setPrefilledSlot({ date, time, therapistId: therapist || null });
      }
      setIsBookingOpen(true);
    }
  }, [searchParams]);

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Physiotherapy",
    "name": "Fizioterapija Saša Panič",
    "description": "Fizioterapija v Ljubljani za športne poškodbe, bolečine v hrbtu, vratu, ramenih in kolenih. Manualna terapija, TECAR, laser, dry needling.",
    "url": "https://sasapanic.com",
    "telephone": "+38640464886",
    "email": "info@sasapanic.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Dunajska cesta 154",
      "addressLocality": "Ljubljana",
      "postalCode": "1000",
      "addressCountry": "SI"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "46.0748",
      "longitude": "14.5056"
    },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "07:00", "closes": "20:00" }
    ],
    "priceRange": "€€",
    "image": "https://sasapanic.com/images/fizioterapevtska-obravnava-pacient.jpg",
    "sameAs": ["https://www.instagram.com/sasapanic_fizioterapija/"]
  };

  const openBooking = (service = null) => {
    setSelectedService(service);
    setIsBookingOpen(true);
  };

  const closeBooking = () => {
    setIsBookingOpen(false);
    setSelectedService(null);
  };

  const openPricing = () => {
    setIsPricingOpen(true);
  };

  const closePricing = () => {
    setIsPricingOpen(false);
  };

  // Listen for global booking event from floating button
  React.useEffect(() => {
    const handleOpenBooking = () => openBooking();
    window.addEventListener('openBookingModal', handleOpenBooking);
    return () => window.removeEventListener('openBookingModal', handleOpenBooking);
  }, []);

  // Listen for pricing modal event from navigation
  React.useEffect(() => {
    const handleOpenPricing = () => openPricing();
    window.addEventListener('openPricingModal', handleOpenPricing);
    return () => window.removeEventListener('openPricingModal', handleOpenPricing);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Fizioterapija Ljubljana | Manualna in športna fizioterapija | Saša Panič</title>
        <meta name="description" content="Fizioterapija v Ljubljani za športne poškodbe, bolečine v hrbtu, vratu, ramenih in kolenih. Naročanje online. Dunajska cesta 154." />
        <meta property="og:title" content="Fizioterapija Ljubljana | Manualna in športna fizioterapija | Saša Panič" />
        <meta property="og:description" content="Fizioterapija v Ljubljani za športne poškodbe, bolečine v hrbtu, vratu, ramenih in kolenih. Naročanje online. Dunajska cesta 154." />
        <meta property="og:url" content="https://sasapanic.com" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://sasapanic.com/" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>
      <Navigation onBookClick={() => openBooking()} onPricingClick={openPricing} />
      <main>
        <HeroSection onBookClick={() => openBooking()} />
        <ConditionsSection onBookClick={() => openBooking()} />
        <QuickHelpSection />
        <ServicesSection onBookService={openBooking} />
        <MonthlyProgramBanner />
        <RehabPackagesSection onBookService={openBooking} />
        <AboutSection />
        <TestimonialsSection />
        <PartnersSection />
        <ContactSection />
      </main>
      <Footer />
      <BookingModal 
        isOpen={isBookingOpen} 
        onClose={closeBooking} 
        preselectedService={selectedService}
        prefilledSlot={prefilledSlot}
      />
      <PricingModal
        isOpen={isPricingOpen}
        onClose={closePricing}
        onBookService={openBooking}
      />
      <CookieBanner />
    </div>
  );
};

export default HomePage;
